import React, { useState } from "react";
import * as yup from "yup";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import Button from "../../ui/Button";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  disputeReview,
  fetchSingleDispute,
  finalDecision,
} from "../../../redux/features/orderSlice";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import ModalInput from "../../ui/ModalTextInput";

const approveDisputeSchema = yup.object().shape({
  defaulter: yup
    .string()
    .oneOf(["VENDOR", "BUYER"])
    .required("Defaulter is required"),

  reason: yup
    .string()
    .trim()
    .min(10, "Reason must be at least 10 characters")
    .required("Reason is required"),
});

const finalDisputeSchema = yup.object().shape({
  defaulter: yup
    .string()
    .oneOf(["VENDOR", "BUYER"])
    .required("Defaulter is required"),

  reason: yup
    .string()
    .trim()
    .min(10, "Reason must be at least 10 characters")
    .required("Reason is required"),

  returnFee: yup.number().when("defaulter", {
    is: "VENDOR",
    then: (schema) => schema.required("Return fee is required for vendors"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const ApproveDisputeModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  modalTitle: string;
}> = ({ isOpen, onClose, modalTitle }) => {
  const { loading, selectedDispute } = useAppSelector((state) => state.orders);
  const [defaulter, setDefaulter] = useState("VENDOR");
  const [reason, setReason] = useState("");
  const [returnFee, setReturnFee] = useState<number | string>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { id } = useParams();
  const dispatch = useAppDispatch();

  const handleSubmit = async () => {
    try {
      await (modalTitle === "Dispute final decision"
        ? finalDisputeSchema
        : approveDisputeSchema
      ).validate({ defaulter, reason, returnFee }, { abortEarly: false });
      setErrors({});

      const details = {
        disputeId: selectedDispute?.id,
        defaulter: defaulter,
        defaulterReason: reason,
      };

      let response;
      if (modalTitle === "Review Dispute") {
        response = await dispatch(disputeReview(details));
      } else if (modalTitle === "Dispute final decision") {
        response = await dispatch(
          finalDecision({ ...details, returnFee: Number(returnFee) })
        );
      }

      // Ensure dispatch was successful before closing the modal
      if (response?.meta?.requestStatus === "fulfilled") {
        toast.success("Dispute successfully processed!");
        dispatch(fetchSingleDispute(id));
        onClose();
      } else {
        toast.error("Failed to process dispute. Please try again.");
      }
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
          toast.error(error.message); // Show validation error messages
        });
        setErrors(validationErrors);
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  return (
    <CustomModalContainer
      open={isOpen}
      onClose={onClose}
      title={modalTitle}
      footer={
        <div className="flex justify-between gap-4">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            customStyle={{
              width: "auto",
              minWidth: "150px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Continue"
            type="button"
            onClick={handleSubmit}
            customStyle={{
              width: "auto",
              minWidth: "150px",
            }}
            isLoading={loading}
          />
        </div>
      }
    >
      {/* Dispute defaulter Selection */}
      <div className="mb-4">
        <label className="block text-gray-700 font-medium mb-1">
          Dispute defaulter <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-4 flex-col">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="defaulter"
              value="VENDOR"
              checked={defaulter === "VENDOR"}
              onChange={() => {
                setDefaulter("VENDOR");
                setErrors({ defaulter: "" });
              }}
              className="accent-indigo-600"
            />
            Vendor
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="defaulter"
              value="BUYER"
              checked={defaulter === "BUYER"}
              onChange={() => setDefaulter("BUYER")}
              className="accent-indigo-600"
            />
            Buyer
          </label>
        </div>
        {errors.defaulter && (
          <p className="text-red-500 text-sm">{errors.defaulter}</p>
        )}
      </div>
      <div className="mb-4">
        {modalTitle === "Dispute final decision" && defaulter === "VENDOR" && (
          <ModalInput
            label="Return fee"
            name="returnFee"
            placeholder="Enter return fee"
            value={returnFee}
            onChange={(e) => {
              e.preventDefault();
              setReturnFee(e.target.value);
              setErrors((prev) => ({ ...prev, returnFee: "" }));
            }}
            type="number"
            required
          />
        )}
        {errors.reason && (
          <p className="text-red-500 text-sm">{errors.reason}</p>
        )}
      </div>

      {/* Reason for Approval */}
      <div className="mb-4">
        <label className="block text-gray-700 font-medium mb-1">
          Reason for resolution <span className="text-red-500">*</span>
        </label>
        <textarea
          className="w-full h-24 border border-gray-300 rounded-md p-2 outline-none resize-none"
          placeholder="Enter a reason for resolution"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setErrors({ reason: "" });
          }}
        />
        {errors.reason && (
          <p className="text-red-500 text-sm">{errors.reason}</p>
        )}
      </div>
    </CustomModalContainer>
  );
};

export default ApproveDisputeModal;
